/* DynamicQuery, returned by createDynamicQuery calls
 .subscribe(event, callback) - subscribe to an event
 .unsubscribe(event, callback) - unsubscribe to an event
 Events:
 inserted (row) - on new row insertion
 updated (row, oldRow) - on row update
 deleted (oldRow) - on row deletion
 done - once the query has finished loading

 .get(id) - get a particular row in the table
 .iterate(callback) - iterate through the rows of the table, calling callback(row) for each row
 */
/* eslint-disable guard-for-in */
export class DynamicQuery {
  done = false;
  reloading = false;
  primaryKey = null;
  rows = {};
  events = {};
  id = this._generateId();

  constructor(public query, public values, private closeRequestCallback, private resetCallback) {
  }

  init(keyField) {
    this.primaryKey = keyField;
    this._fireEvent('inited');
  }

  updateRow(row) {
    const rowId = row[this.primaryKey];
    const oldRow = this.rows[rowId];
    if (!oldRow) {
      this.rows[rowId] = this._cloneRow(row);
      this._fireEvent('inserted', row);
    } else {
      const changed = this._getChangedCells(oldRow, row);
      if (changed) {
        this.rows[rowId] = this._mergeRows(oldRow, row);
        this._fireEvent('updated', this.rows[rowId], oldRow, changed);
      }
    }
  }
  get(id) {
    return this.rows[id];
  }

  deleteRow(id) {
    const oldRow = this.rows[id];
    if (oldRow) {
      this.rows[id] = void 0;
      this._fireEvent('deleted', oldRow);
    }
  }

  getPrimaryKey() {
    return this.primaryKey;
  }

  setDone() {
    if (!this.done) {
      this.done = true;
      this._fireEvent('done');
    }
  }

  iterate(callback) {
    for (let i in this.rows) {
      if (this.rows[i]) {
        callback(this.rows[i]);
      }
    }
  }

  purge() {
    for (let id in this.rows) {
      this.deleteRow(id);
    }
  }

  addListener(event, callback) {
    let eventHandlers = this.events[event];
    if (!eventHandlers) {
      eventHandlers = [];
      this.events[event] = eventHandlers;
    }
    eventHandlers.push(callback);
  }

  removeListener(event, callback) {
    const eventHandlers = this.events[event];
    if (eventHandlers) {
      const index = eventHandlers.indexOf(callback);
      if (index >= 0) {
        eventHandlers.splice(index, 1);
      }
    }
  }

  isDone() {
    return this.done;
  }

  isReloading() {
    return this.reloading;
  }

  close() {
    return this.closeRequestCallback(this);
  }

  reset(queryName, valuesParam) {
    this.query = queryName;
    this.values = valuesParam;
    let promise = this.resetCallback(this);
    if (!this.reloading) {
      this.reloading = true;
      promise = promise
        .then(dq => {
          this.reloading = false;
          return dq;
        });
    }
    return promise;
  }

  // code smell: seems these args are unused
  private _fireEvent(event, _arg1?, _arg2?, _arg3?) {
    const eventHandlers = this.events[event];
    const args = Array.prototype.slice.call(arguments, 1);

    if (eventHandlers) {
      [].concat(eventHandlers).forEach(handler => {
        handler.apply(this, args);
      });
    }
  }

  private _generateId() {
    const highValue = Math.floor(Math.random() * 9223372037);
    return ((highValue === 0) ? '' : highValue) + '' + Math.floor(Math.random() * ((highValue === 9223372036) ? 854775808 : 1000000000));
  }

  private _mergeRows(oldRow, row) {
    const updatedRow = this._cloneRow(oldRow);

    for (let i in row) {
      updatedRow[i] = row[i];
    }

    return updatedRow;
  }

  private _cloneRow(rowToClone) {
    const clone = {};
    for (let i in rowToClone) {
      clone[i] = rowToClone[i];
    }
    return clone;
  }

  private _getChangedCells(oldRow, row) {
    let changed = null;
    for (let i in oldRow) {
      if (oldRow[i] !== row[i]) {
        changed = changed || {};
        changed[i] = row[i];
      }
    }
    return changed;
  }

}
